/**
 * Take back an edit signal the person typed over (RFC 0301 S13).
 *
 * The pause fires after four seconds of quiet, and four seconds is short on
 * purpose: when the thought was finished the agent answers fast. When it was
 * not, the person types again and the turn that was queued is about a
 * half-typed sentence. So the app that delivered the edit withdraws it on the
 * next keystroke, and the next pause delivers the whole change from where the
 * withdrawn one began.
 *
 * ONLY A TURN THAT HAS SAID NOTHING is withdrawn. Once the agent has written a
 * word or made a call, the turn is a real answer and stopping it would throw
 * away work the person can see. The test is the log after the signal: an
 * assistant message with text, or a tool use, means it spoke.
 *
 * Cancelling is `cancelRun`, the stop button's path, so a harness mid-turn is
 * interrupted the way a person's stop interrupts it. The run reads `cancelled`
 * on the log, and the firing that started it still points at it, so how often
 * the pause fires early is a fold over firings whose run was cancelled.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { decodeLogDoc } from '../../log/codec'
import { projectFirings } from '../../log/projection-firings'
import { topoOrder } from '../../log/projection-order'
import type { EntryId, LogDoc } from '../../log/types'
import { cancelRun } from '../run/cancel-run'

/** Did the agent say or do anything visible after this signal? */
export function spokeAfter(doc: LogDoc, entryId: EntryId): boolean {
  let after = false
  for (const e of topoOrder(doc)) {
    if (e.id === entryId) {
      after = true
      continue
    }
    if (!after) continue
    const p = e.payload
    if (p.type !== 'message') continue
    const m = p.payload
    if (m.role !== 'assistant') continue
    if ((m.text ?? '').trim().length > 0) return true
    if ((m.toolUses ?? []).length > 0) return true
  }
  return false
}

/**
 * Cancel the turn the signal `entryId` started, if it is still in flight and
 * has produced nothing visible. Returns true when a run was cancelled.
 */
export async function withdrawEdit(
  repo: Repo,
  sessionId: string,
  entryId: EntryId,
): Promise<boolean> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = decodeLogDoc(handle.doc() as Record<string, unknown>)
  const firing = projectFirings(doc).find((f) => f.entryId === entryId)
  if (!firing || !firing.runId) return false
  if (
    firing.runStatus !== 'queued' &&
    firing.runStatus !== 'running' &&
    firing.runStatus !== 'waiting_for_tools'
  ) {
    return false
  }
  if (spokeAfter(doc, entryId)) return false
  const cancelled = await cancelRun(repo, sessionId, { runId: firing.runId })
  return cancelled !== null
}
